import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '../icons/Icon'
import { StatusPill } from '../StatusPill'
import { cancelBackgroundJob, getJobErrorReportUrl } from '../../api/jobs'
import { ApiError } from '../../api/problem'
import { KIND_LABEL, STATUS_VARIANT, formatDateTime, statusLabel, type BackgroundJobView } from '../../types/job'
import { resolveJobTarget } from './resolveJobTarget'

const CANCELLABLE_STATUSES = new Set(['queued', 'running'])

export interface JobRowProps {
  job: BackgroundJobView
  /** Called with the SERVER's own returned view after a successful cancel (`202`) — never an
   * optimistic client-guessed state (AC6). */
  onUpdated: (updated: BackgroundJobView) => void
}

function actionErrorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/** One row in the Jobs tray (design §4): kind, status, progress/counts, timestamp, and
 * real-or-absent actions (never disabled-and-inert — CLAUDE.md §4). */
export function JobRow({ job, onUpdated }: JobRowProps) {
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [reportError, setReportError] = useState<string | null>(null)

  const kindLabel = KIND_LABEL[job.kind] ?? job.kind
  const isCancellable = CANCELLABLE_STATUSES.has(job.status)
  const hasErrorReport = job.errorReportRef !== null
  const target = resolveJobTarget(job)

  function handleCancel() {
    setCancelling(true)
    setCancelError(null)
    cancelBackgroundJob(job.id)
      .then((updated) => onUpdated(updated))
      .catch((err) => setCancelError(actionErrorMessage(err, 'Could not cancel this job.')))
      .finally(() => setCancelling(false))
  }

  function handleDownloadReport() {
    setReportError(null)
    getJobErrorReportUrl(job.id)
      .then(({ url }) => {
        window.open(url, '_blank', 'noopener')
      })
      .catch((err) => setReportError(actionErrorMessage(err, 'The error report is unavailable.')))
  }

  return (
    <li className="iw-job-row">
      <div className="iw-job-row__main">
        <div className="iw-job-row__heading">
          <span className="iw-job-row__kind">{kindLabel}</span>
          <StatusPill variant={STATUS_VARIANT[job.status] ?? 'neutral'} label={statusLabel(job.status)} />
        </div>

        {job.status === 'running' ? (
          <div
            className="iw-job-row__progress"
            role="progressbar"
            aria-label={`${kindLabel} progress`}
            aria-valuemin={0}
            aria-valuemax={100}
            {...(job.percent != null ? { 'aria-valuenow': job.percent } : {})}
          >
            <div className="iw-job-row__progress-track">
              {job.percent != null ? (
                <div className="iw-job-row__progress-fill" style={{ width: `${job.percent}%` }} />
              ) : (
                <div className="iw-job-row__progress-fill iw-job-row__progress-fill--indeterminate" />
              )}
            </div>
            <span className="iw-job-row__phase">
              {job.phase ?? 'Running…'}
              {job.percent != null ? ` · ${job.percent}%` : ''}
            </span>
          </div>
        ) : job.status === 'queued' ? (
          <span className="iw-job-row__phase">Queued</span>
        ) : (
          <span className="iw-job-row__counts">
            {job.counts.succeeded} succeeded · {job.counts.failed} failed · {job.counts.skipped} skipped (of{' '}
            {job.counts.total})
          </span>
        )}

        <span className="iw-job-row__timestamp">{formatDateTime(job.completedAt ?? job.startedAt ?? job.createdAt)}</span>

        {cancelError ? (
          <span className="iw-job-row__error" role="alert">
            {cancelError}
          </span>
        ) : null}
        {reportError ? (
          <span className="iw-job-row__error" role="alert">
            {reportError}
          </span>
        ) : null}
      </div>

      <div className="iw-job-row__actions">
        {isCancellable ? (
          <button
            type="button"
            className="iw-btn iw-btn--sm iw-btn--ghost"
            onClick={handleCancel}
            disabled={cancelling}
          >
            <Icon name="x" size={12} />
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </button>
        ) : null}
        {hasErrorReport ? (
          <button type="button" className="iw-btn iw-btn--sm iw-btn--ghost" onClick={handleDownloadReport}>
            <Icon name="download" size={12} />
            Error report
          </button>
        ) : null}
        {target ? (
          <Link to={target.to} className="iw-btn iw-btn--sm iw-btn--ghost">
            <Icon name="chevron-right" size={12} />
            Open target
          </Link>
        ) : null}
      </div>
    </li>
  )
}
